import type { ISiteTagsDto } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import type { ISiteTag } from "app/pages/sales-intelligence/domain/leads/types/website";

import { SiteTagsFilterValue } from "../../../../models/SiteTagsFilter/SiteTagsFilterValue";

export const MAX_SITE_TAGS_BULK_VALIDATION = 500;

export function parseSiteTagsBulkValue(value: string): string[] {
    return value
        .split(/\r?\n|\s*[,|]\s*/)
        .map((part) => part.trim())
        .filter(Boolean);
}

export function countUniquePreparedSiteTags(tags: readonly string[]): number {
    const seen = new Set<string>();

    for (const tag of tags) {
        const trimmed = tag.trim();

        if (!trimmed) {
            continue;
        }

        seen.add(trimmed.toLowerCase());
    }

    return seen.size;
}

export function prepareSiteTagsForValidation(tags: readonly string[]): string[] {
    const seen = new Set<string>();
    const prepared: string[] = [];

    for (const tag of tags) {
        const trimmed = tag.trim();

        if (!trimmed) {
            continue;
        }

        const dedupeKey = trimmed.toLowerCase();

        if (seen.has(dedupeKey)) {
            continue;
        }

        seen.add(dedupeKey);
        prepared.push(trimmed);

        if (prepared.length >= MAX_SITE_TAGS_BULK_VALIDATION) {
            break;
        }
    }

    return prepared;
}

function mergeSiteTagValuesCaseInsensitive(existing: readonly string[], toAdd: readonly string[]): string[] {
    const byLower = new Map<string, string>();

    for (const tag of existing) {
        byLower.set(tag.toLowerCase(), tag);
    }

    for (const tag of toAdd) {
        const key = tag.toLowerCase();

        if (!byLower.has(key)) {
            byLower.set(key, tag);
        }
    }

    return Array.from(byLower.values());
}

export function mapValidatedSiteTagsResponse(response: readonly ISiteTag[]): string[] {
    return response.map(({ tag }) => tag);
}

export function getInvalidBulkSiteTags(
    preparedTags: readonly string[],
    validatedTags: readonly string[],
): readonly string[] {
    const validated = new Set(validatedTags.map((tag) => tag.toLowerCase()));

    return preparedTags.filter((tag) => !validated.has(tag.toLowerCase()));
}

export const getValidatedBulkSiteTagsForPrepared = (
    preparedTags: readonly string[],
    validatedTags: readonly string[],
): readonly string[] => {
    const validatedByLower = new Map(validatedTags.map((tag) => [tag.toLowerCase(), tag]));

    return preparedTags
        .map((tag) => validatedByLower.get(tag.toLowerCase()))
        .filter((tag): tag is string => tag !== undefined);
};

export function areSiteTagListsEqual(first: readonly string[], second: readonly string[]): boolean {
    return first.length === second.length && first.every((tag, index) => tag === second[index]);
}

export const mergeSiteTagsBulk = ({
    item,
    preparedTags,
    validatedTags,
}: {
    item: ISiteTagsDto;
    preparedTags: readonly string[];
    validatedTags: readonly string[];
}): ISiteTagsDto => {
    const tagsToMerge = getValidatedBulkSiteTagsForPrepared(preparedTags, validatedTags);

    if (tagsToMerge.length === 0) {
        return item;
    }

    const mergedValues = mergeSiteTagValuesCaseInsensitive(item.values, tagsToMerge);
    const nextValue = new SiteTagsFilterValue(item).with({ values: mergedValues });

    return nextValue!;
};
